import { Flex } from '@buffer-mono/popcorn'

import type { useCommentsChannels } from '~publish/components/CommentsSidebar/useCommentsChannels'
import { readFragment } from '~publish/graphql'
import { CommentsPostCard_Post } from '~publish/pages/Community/fragments/CommentsPostCard_Post'

import { CommentContextProvider } from '../Comments/context/CommentContext'
import { CommentThreadContent } from '../Comments/CommentCard/CommentThreadContent'
import { CommentsContextSection } from '../Comments/CommentsContextSection'
import { PostMiniature } from '../PostMiniature'
import type { CommentThreadData } from './types'

import styles from './CommentDetailsContent.module.css'

import type { JSX } from 'react'

type Channel = ReturnType<typeof useCommentsChannels>['channels'][number]

type CommentDetailsContentProps = {
  originalComment: NonNullable<CommentThreadData['originalComment']>
  channel: Channel
}

/**
 * The body of the comment-details dialog: the post the comment belongs to,
 * the comment's ancestors (when it's a nested reply — collapsed behind the
 * same "Show more replies" incremental expansion as the inbox card), and the
 * comment itself with its replies and the reply form.
 *
 * Reuses the inbox thread components so the dialog stays visually and
 * behaviorally consistent with the Community inbox.
 */
export function CommentDetailsContent({
  originalComment,
  channel,
}: CommentDetailsContentProps): JSX.Element {
  const post = readFragment(CommentsPostCard_Post, originalComment.post)

  return (
    <Flex direction="column" gap="md" className={styles.container}>
      <PostMiniature post={post} className={styles.post} />
      <div className={styles.thread}>
        <CommentContextProvider comment={originalComment}>
          <CommentsContextSection channel={channel} />
          <CommentThreadContent
            channel={channel}
            expanded={true}
            // Opening the dialog is always an intentional focus on this
            // comment, so replied/resolved comments keep their reply form —
            // the same behavior as interim-expanding a resolved inbox card.
            isInterimExpanded={true}
          />
        </CommentContextProvider>
      </div>
    </Flex>
  )
}
